'use client';

import Link from '@/components/ui/Link';
import PhotoCarousel from '@/components/component_unique/PhotoCarousel';
import AudioPlayer from '@/components/component_unique/AudioPlayer';
import TweetEmbed from '@/components/component_unique/TweetEmbed';
import InstagramEmbed from '@/components/component_unique/InstagramEmbed';
import VideoEmbed from '@/components/component_unique/VideoEmbed';
import GumletEmbed from '@/components/component_unique/GumletEmbed';
import ChipsForm from '@/components/component_form/ChipsForm';
import JustIn from '@/components/component_unique/JustIn';
import VuukleComments from '@/components/ads/VuukleComments';
import YouTubeThumbnail from '@/components/component_unique/YouTubeThumbnail';
import { MessageIconLogo } from '@/components/ui/message-icon-logo';
import { BOOKMARK_CATEGORIES } from '@/lib/api/public-client';
import FileAttachment from '@/components/component_unique/FileAttachment';
import { getImageUrl } from '@/lib/api';
import { processArticleLinks } from '@/lib/linkUtils';
import { sanitizeToText } from '@/lib/sanitize';
import { computeParagraphMap } from '@/lib/articleInlineAds';
const HIDDEN_CATEGORY_NAMES = ['விளம்பரதாரர் செய்திகள்'];
export interface ArticlePhotoBodyProps {
  story: any;
  index: number;
  activeAdArticleIndex: number;
  category: string;
  sectionUrl: string;
  imageUrl: string;
  updatedAt: string;
  showAudioPlayer: boolean;
  promotionBlock: React.ReactNode;
  renderInlineAd?: (globalIdx: number, kind?: 'text' | 'image') => React.ReactNode;
  showRelatedStory?: boolean;
  showLinkedStories?: boolean;
  showStoryTags?: boolean;
  showComments?: boolean;
  /** Whether to render this component's own headline + PhotoCarousel + author/
   *  share header. The dynamic article-template renderer already shows
   *  headline/hero/author/dateMeta/social via ArticleHeroSection, so it
   *  passes showHero={false} — same convention as ArticleTextBody. */
  showHero?: boolean;
}

/**
 * The `photo` article body — extracted from the `story['story-template']
 * === 'photo'` branch in src/app/api/articles/ArticleListInfinite.tsx. The
 * defining feature vs. the generic text body: the hero + all story images
 * render as a single swipeable PhotoCarousel instead of a stacked list of
 * full-width images down the page.
 */
export default function ArticlePhotoBody({
  story,
  index,
  activeAdArticleIndex,
  category,
  sectionUrl,
  imageUrl,
  updatedAt,
  showAudioPlayer,
  promotionBlock,
  renderInlineAd,
  showRelatedStory = true,
  showLinkedStories = true,
  showStoryTags = true,
  showComments = true,
  showHero = true
}: ArticlePhotoBodyProps) {
  const tagNames = story.tags?.map((tag: any) => ({
    name: tag.name,
    slug: tag.slug
  }));
  const inlineAlsoReadIds = new Set<string>();
  story.cards?.forEach((card: any) => {
    card['story-elements']?.forEach((el: any) => {
      if (el.subtype === 'also-read') {
        const id = el.metadata?.['linked-story-id'] || el['linked-story-id'];
        if (id) inlineAlsoReadIds.add(id);
      }
    });
  });
  return <>
      {/* Both the headline/subheadline block AND the carousel are gated by
          showHero now: when false (the dynamic renderer), ArticleHeroSection's
          heroImage field renders the full PhotoCarousel itself (all story
          images, not just hero-image-s3-key) in its proper hero position —
          see the templateType === 'photo' branch in ArticleHeroSection.tsx.
          Rendering it again here would duplicate it, same lesson as
          ArticleGumletVideoBody/ArticleVideoBody. */}
      {showHero && <>
          <div className="relative p-4 border-b border-DCDCDC">
            <div className="absolute top-4 right-4 z-10 flex items-center gap-1">
              <MessageIconLogo id={`photo-${story.slug}`} variant="message-icon-3" slug={story.slug} category={BOOKMARK_CATEGORIES.PHOTOS} title={story.headline} thumbnailUrl={imageUrl} description={story.subheadline} articleUrl={`/${story.slug}`} />
            </div>
            {category && !HIDDEN_CATEGORY_NAMES.includes(category) && <Link href={sectionUrl || '#'}>
                <h2 className="text-16-tamil-600 text-012061 mb-2 hover:underline cursor-pointer">{category}</h2>
              </Link>}
            <h1 className="text-24-tamil-700 text-1B1B1B mb-2">{story.headline}</h1>
            {story.subheadline && <h2 className="text-18-tamil-400 text-4D4D4D mb-2">{story.subheadline}</h2>}
          </div>

          <div className="border-b border-DCDCDC">
            <PhotoCarousel heroImage={{
          imageS3Key: story['hero-image-s3-key'],
          imageMetadata: story['hero-image-metadata'],
          title: story['hero-image-caption'] || '',
          attribution: story['hero-image-attribution'] || ''
        }} photos={story.cards?.flatMap((card_0: any) => card_0['story-elements']?.map((element: any) => ({
          id: `${card_0.id}-${element.id}`,
          title: element.title,
          imageS3Key: element['image-s3-key'],
          text: element.text,
          imageMetadata: element['image-metadata'],
          attribution: element['image-attribution'] || ''
        })) || [])} maxHeight={575} containerClassName="aspect-[16/9] max-h-[400px]" />
          </div>
        </>}

      {/* No author/date/share row here at all: photo's author name is always
          a static, unbound Builder placeholder ("Author Name", no real
          content); the date is already covered by ArticleHeroSection's
          dateMeta field; and share icons are already covered by its
          socialShare field. All three would be either meaningless or a
          duplicate of the hero section in the body. */}
      {promotionBlock}
      {showAudioPlayer && <AudioPlayer />}
      {story['embed-js'] && <div className="border-b border-DCDCDC p-4">
          {(() => {
        try {
          const decoded = atob(story['embed-js']);
          if (decoded.includes('twitter-tweet') || decoded.includes('platform.twitter.com')) {
            return <TweetEmbed embedJs={story['embed-js']} />;
          }
          if (decoded.includes('instagram-media') || decoded.includes('instagram.com')) {
            return <InstagramEmbed embedJs={story['embed-js']} />;
          }
        } catch {
          if (story['embed-js'].includes('twitter-tweet') || story['embed-js'].includes('platform.twitter.com')) {
            return <TweetEmbed embedJs={story['embed-js']} />;
          }
          if (story['embed-js'].includes('instagram-media') || story['embed-js'].includes('instagram.com')) {
            return <InstagramEmbed embedJs={story['embed-js']} />;
          }
        }
        return <GumletEmbed embedJs={story['embed-js']} />;
      })()}
        </div>}
      {story['embed-url'] && <div className="border-b border-DCDCDC">
          <YouTubeThumbnail embedUrl={story['embed-url']} thumbnailUrl={imageUrl} title={story.headline} />
        </div>}
      <div className="p-4 space-y-6">
        {/* afterParagraph: 0 means "before the first paragraph" — see buildParagraphAdPlan. */}
        {renderInlineAd?.(-1)}
        {(() => {
        const paraMap = computeParagraphMap(story);
        return story.cards?.map((card_1: any, cardIdx: number) => <div key={cardIdx}>
            {card_1['story-elements']?.map((element_0: any, elementIdx: number) => {
            const paraInfo = paraMap[`${cardIdx}_${elementIdx}`];
            const isRegularText = element_0.text && element_0.subtype !== 'summary' && element_0.subtype !== 'also-read' && element_0.subtype !== 'quote' && element_0.subtype !== 'blockquote' && element_0.subtype !== 'cta' && element_0.subtype !== 'q-and-a' && element_0.subtype !== 'bigfact' && element_0.subtype !== 'blurb' && element_0.subtype !== 'question' && element_0.type === 'text' && !element_0.metadata?.['promotional-message'];
            return <div key={elementIdx} className="mb-6">
                {element_0.subtype === 'summary' && element_0.text && <div>
                    <h3 className="text-18-tamil-700 text-1B1B1B mb-2">சுருக்கம்</h3>
                    <div className="text-18-tamil-400 text-1B1B1B mb-4 article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                  __html: processArticleLinks(element_0.text)
                }} />
                  </div>}
                {element_0.subtype === 'quote' && element_0.text && <div className="my-4 quote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
                __html: processArticleLinks(element_0.text)
              }} />}
                {element_0.subtype === 'blockquote' && element_0.text && <div className="my-4 blockquote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
                __html: processArticleLinks(element_0.text)
              }} />}
                {isRegularText && paraInfo && paraInfo.paragraphs.length > 0 && paraInfo.paragraphs.map((paraHtml: string, paraIdx: number) => {
                const globalIdx = paraInfo.startIndex + paraIdx;
                return <div key={`p-${paraIdx}`}>
                        <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(paraHtml)
                  }} />
                        {renderInlineAd?.(globalIdx)}
                      </div>;
              })}
                {isRegularText && (!paraInfo || paraInfo.paragraphs.length === 0) && <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                __html: processArticleLinks(element_0.text)
              }} />}
                {element_0.text && element_0.metadata?.['promotional-message'] && <div className="text-14-tamil-400 article-links [&_p]:mb-4" style={{
                color: '#00000099'
              }} dangerouslySetInnerHTML={{
                __html: processArticleLinks(element_0.text)
              }} />}
                {element_0.type === 'file' && element_0['content-type'] !== 'application/pdf' && element_0.url && element_0['file-name'] && <div className="mb-4">
                    <FileAttachment fileName={element_0['file-name']} url={element_0.url} fileSize={element_0.metadata?.['file-size']} />
                  </div>}
                {element_0.type === 'composite' && element_0.subtype === 'image-gallery' && element_0['story-elements']?.length > 0 && (element_0.metadata?.type === 'slideshow' ? <div className="my-4">
                      <PhotoCarousel photos={element_0['story-elements'].filter((img: any) => img['image-s3-key']).map((img_0: any) => ({
                  id: img_0.id,
                  title: img_0.title,
                  imageS3Key: img_0['image-s3-key'],
                  imageMetadata: img_0['image-metadata'],
                  attribution: img_0['image-attribution']
                }))} maxHeight={400} />
                    </div> : <div className="my-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {element_0['story-elements'].map((img_1: any, imgIdx: number) => img_1['image-s3-key'] && <div key={img_1.id || imgIdx}>
                          <img src={getImageUrl(img_1['image-s3-key'])} alt={img_1['alt-text'] || img_1.title || 'Gallery image'} className="w-full h-[150px] object-cover rounded" loading="lazy" decoding="async" />
                          {img_1['image-attribution'] && <p className="text-12-tamil-400 mt-1" style={{
                    color: '#00000099'
                  }}>{sanitizeToText(img_1['image-attribution'])}</p>}
                        </div>)}
                    </div>)}
                {element_0.type === 'composite' && element_0.subtype === 'references' && element_0['story-elements']?.length > 0 && <div className="my-4 border border-DCDCDC rounded-lg p-4 bg-gray-50">
                    <h4 className="text-16-tamil-700 text-1B1B1B mb-2">குறிப்புகள்</h4>
                    <ul className="space-y-2 list-disc pl-5">
                      {element_0['story-elements'].map((ref: any, refIdx: number) => <li key={ref.id || refIdx} className="text-14-tamil-400 text-1B1B1B">
                          {ref.metadata?.url ? <a href={ref.metadata.url} target="_blank" rel="noopener noreferrer" className="text-012061 hover:underline">
                              {ref.metadata?.name || 'Reference'}
                            </a> : <span>{ref.metadata?.name}</span>}
                          {ref.metadata?.description && <span className="block text-12-tamil-400" style={{
                      color: '#00000099'
                    }}>{ref.metadata.description}</span>}
                        </li>)}
                    </ul>
                  </div>}
                {element_0.subtype === 'cta' && element_0.metadata?.['cta-url'] && <div className="my-4 text-center">
                    <a href={element_0.metadata['cta-url']} target={element_0.metadata['open-in-new-tab'] ? '_blank' : '_self'} rel="noopener noreferrer" className="inline-block px-6 py-3 bg-012061 text-white rounded-full text-16-tamil-600 hover:opacity-90 transition-opacity">
                      {element_0.metadata['cta-title'] || element_0.text}
                    </a>
                  </div>}
                {element_0.subtype === 'q-and-a' && (element_0.metadata?.question || element_0.metadata?.answer) && <div className="my-4 space-y-3">
                    {element_0.metadata?.question && <div style={{
                  backgroundColor: '#E3F2FD',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                        <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                          <span style={{
                      backgroundColor: '#1565C0',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>Q</span>
                          <div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: processArticleLinks(element_0.metadata.question)
                    }} />
                        </div>
                      </div>}
                    {element_0.metadata?.answer && <div style={{
                  backgroundColor: '#FFF3E0',
                  padding: '16px',
                  borderRadius: '8px'
                }}>
                        <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                          <span style={{
                      backgroundColor: '#E65100',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>A</span>
                          <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-0" style={{
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: processArticleLinks(element_0.metadata.answer)
                    }} />
                        </div>
                      </div>}
                  </div>}
                {element_0.subtype === 'question' && element_0.text && <div className="my-4" style={{
                backgroundColor: '#E3F2FD',
                padding: '16px',
                borderRadius: '8px'
              }}>
                    <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                      <span style={{
                    backgroundColor: '#1565C0',
                    color: 'white',
                    fontWeight: 'bold',
                    minWidth: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    fontSize: '16px',
                    flexShrink: 0
                  }}>Q</span>
                      <div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                    flex: 1
                  }} dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element_0.text)
                  }} />
                    </div>
                  </div>}
                {element_0.subtype === 'bigfact' && (element_0.metadata?.content || element_0.text) && <div className="my-6 text-center border-y border-DCDCDC py-6">
                    <div className="text-32-tamil-700 text-012061">{element_0.metadata?.content}</div>
                    {element_0.metadata?.attribution && <div className="mt-2 text-14-tamil-400" style={{
                  color: '#00000099'
                }}>{element_0.metadata.attribution}</div>}
                  </div>}
                {element_0.subtype === 'blurb' && element_0.text && <div className="my-4 bg-gray-50 rounded-lg p-4 article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-600 [&_blockquote]:italic [&_blockquote]:text-1B1B1B" dangerouslySetInnerHTML={{
                __html: processArticleLinks(element_0.text)
              }} />}
                {element_0.subtype === 'also-read' && element_0.text && (() => {
                const linkedStoryId = element_0.metadata?.['linked-story-id'] || element_0['linked-story-id'];
                const linkedStory = linkedStoryId && story['linked-stories']?.[linkedStoryId] || element_0.metadata?.['linked-story'];
                if (linkedStory && linkedStory.slug) {
                  return <a href={`/${linkedStory.slug}`} className="flex flex-col sm:flex-row gap-3 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow my-3 p-4">
                        {linkedStory['hero-image-s3-key'] && <div className="w-full sm:w-[150px] h-[160px] sm:h-[100px] flex-shrink-0">
                            <img src={getImageUrl(linkedStory['hero-image-s3-key'])} alt={linkedStory.headline} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                          </div>}
                        <div className="p-3 flex-1 flex items-center">
                          <h4 className="text-16-tamil-700 text-1B1B1B">
                            {linkedStory.headline}
                          </h4>
                        </div>
                      </a>;
                }
                return null;
              })()}
              </div>;
          })}
          </div>);
      })()}

        {showLinkedStories && story['linked-stories'] && Object.keys(story['linked-stories']).length > 0 && <div className="mb-6">
            <div className="grid grid-cols-1 gap-4">
              {Object.entries(story['linked-stories']).filter(([key]) => !inlineAlsoReadIds.has(key)).map(([key_0, linkedStory_0]: [string, any]) => <a key={key_0} href={`/${linkedStory_0.slug}`} className="flex flex-col lg:flex-row gap-3 lg:gap-4 p-4 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow">
                  {linkedStory_0['hero-image-s3-key'] && <div className="w-full lg:w-[200px] h-[200px] lg:h-[120px] flex-shrink-0">
                      <img src={getImageUrl(linkedStory_0['hero-image-s3-key'])} alt={linkedStory_0.headline} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>}
                  <div className="p-3 lg:p-4 flex-1 flex items-center">
                    <h4 className="text-16-tamil-700 lg:text-18-tamil-700 text-1B1B1B">
                      {linkedStory_0.headline}
                    </h4>
                  </div>
                </a>)}
            </div>
          </div>}
      </div>
      {index === 0 && showRelatedStory && <JustIn limit={5} />}
      {showStoryTags && tagNames && tagNames.length > 0 && <div className="border-b border-DCDCDC"><ChipsForm chips={tagNames} /></div>}
      {showComments && Math.abs(index - activeAdArticleIndex) <= 1 && <div style={{
      display: index === activeAdArticleIndex ? 'block' : 'none'
    }} aria-hidden={index !== activeAdArticleIndex}>
          <p style={{
        fontSize: '12px',
        color: '#666',
        lineHeight: 1.6,
        padding: '12px',
        background: '#f9f9f9',
        borderRadius: '6px',
        borderLeft: '3px solid #012061',
        margin: '12px 16px'
      }}>பின்னூட்டத்தில் வெளியாகும் கருத்துகளுக்கு அவற்றைப் பதிவிடுவோரே முழுப் பொறுப்பு; அவை தினமணியின் கருத்துகளைப் பிரதிபலிக்கவில்லை.தனிநபர், சமூகம், மதம் அல்லது நாடு ஆகியவற்றுக்கு எதிராக அவமதிக்கிற அல்லது ஆபாசமான விதத்திலுள்ள எந்தவொரு கருத்தும் இந்திய அரசின் தகவல் தொழில்நுட்பக் கொள்கைப்படி தண்டனைக்குரிய குற்றம். இதுபோன்ற கருத்துகளுக்கு எதிராக உரிய சட்ட நடவடிக்கை எடுக்கப்படும்.</p>

          <div className="border-b border-DCDCDC p-4">
            <VuukleComments key={`vuukle-${story.id}`} articleId={story.id} articleTitle={story.headline || ''} articleUrl={`https://www.dinamani.com/${story.slug}`} articleImage={imageUrl} articleTags={story.tags?.slice(0, 3).map((tag_0: any) => tag_0.name).join(',') || category} articleAuthor={story.authors?.[0]?.name || 'Dinamani'} />
          </div>
        </div>}
    </>;
}